import React, { useState, useRef } from 'react';
import { Button, Typography, Progress, message, Spin, Alert } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  addPhoto,
  updatePhotoStatus,
  PhotoTypes,
  getRequiredPhotoTypes,
  getOptionalPhotoTypes,
  getCompletedRequiredPhotosCount,
  getRequiredPhotosCount,
  getNotReadyRequiredPhotoTypes,
  isPhotoUploadReady,
  scrubBlobUrl,
} from '../../../redux/reducers/vehiclePhotosSlice';
import Api from '../../../services/api';
import Insights from '../../../services/insightsService';
import './PhotoCaptureStep.css';

import imgWide from '../../../assets/images/wide.png';
import imgClose from '../../../assets/images/close.png';
import imgLf from '../../../assets/images/lf.png';
import imgRf from '../../../assets/images/rf.png';
import imgRr from '../../../assets/images/rr.png';
import imgLr from '../../../assets/images/lr.png';
import imgVin from '../../../assets/images/vin.png';
import imgOdometer from '../../../assets/images/odometer.png';
import imgLic from '../../../assets/images/lic.png';

const { Title, Text } = Typography;

type ImageIcon = 'wide' | 'close' | 'lf' | 'rf' | 'rr' | 'lr' | 'vin' | 'odometer' | 'lic';

const PHOTO_ICON_MAP: Record<ImageIcon, string> = {
  wide:     imgWide,
  close:    imgClose,
  lf:       imgLf,
  rf:       imgRf,
  rr:       imgRr,
  lr:       imgLr,
  vin:      imgVin,
  odometer: imgOdometer,
  lic:      imgLic,
};

interface PhotoCaptureStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

interface PhotoCard {
  type: PhotoTypes;
  label: string;
  required: boolean;
  iconName?: ImageIcon;
}

const PhotoCaptureStep: React.FC<PhotoCaptureStepProps> = ({
  onNext,
  onPrevious
}) => {
  const dispatch = useAppDispatch();
  const { photoMap } = useAppSelector((state) => state.vehiclePhotos);
  const [currentPhotoType, setCurrentPhotoType] = useState<PhotoTypes | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const requiredPhotos = getRequiredPhotoTypes();
  const optionalPhotos = getOptionalPhotoTypes();
  const completedRequired = getCompletedRequiredPhotosCount(photoMap);
  const totalRequired = getRequiredPhotosCount();
  const progressPercent = Math.round((completedRequired / totalRequired) * 100);
  const notReadyRequired = getNotReadyRequiredPhotoTypes(photoMap);
  const hasInFlightRequired = getRequiredPhotoTypes().some((type) => {
    const status = photoMap[type]?.uploadStatus;
    return status === 'pending' || status === 'uploading';
  });

  const photoCardIcons: { title: PhotoTypes; iconName: ImageIcon }[] = [
    { title: PhotoTypes['Damage Wide-angle'], iconName: 'wide' },
    { title: PhotoTypes['Damage Close-up'], iconName: 'close' },
    { title: PhotoTypes['Front Driverside'], iconName: 'lf' },
    { title: PhotoTypes['Front Passenger'], iconName: 'rf' },
    { title: PhotoTypes['Rear Driverside'], iconName: 'rr' },
    { title: PhotoTypes['Rear Passenger'], iconName: 'lr' },
    { title: PhotoTypes.Vin, iconName: 'vin' },
    { title: PhotoTypes.Odometer, iconName: 'odometer' },
    { title: PhotoTypes['Lic Plate'], iconName: 'lic' },
    { title: PhotoTypes['Additional Photo 1'], iconName: 'wide' },
    { title: PhotoTypes['Additional Photo 2'], iconName: 'wide' },
    { title: PhotoTypes['Additional Photo 3'], iconName: 'wide' },
    { title: PhotoTypes['Additional Photo 4'], iconName: 'wide' },
  ];

  const photoCards: PhotoCard[] = [
    ...requiredPhotos.map(type => {
      const cardIcon = photoCardIcons.find(icon => icon.title === type);
      return { type, label: PhotoTypes[type], required: true, iconName: cardIcon?.iconName };
    }),
    ...optionalPhotos.map(type => {
      const cardIcon = photoCardIcons.find(icon => icon.title === type);
      return { type, label: PhotoTypes[type], required: false, iconName: cardIcon?.iconName };
    })
  ];

  const handleCardTap = (type: PhotoTypes) => {
    setCurrentPhotoType(type);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const compressImage = async (file: File | Blob, maxWidth = 1920, maxHeight = 1080, quality = 0.8): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Compression failed')),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  const uploadPhotoToAzure = async (photoType: PhotoTypes, blob: Blob): Promise<string> => {
    try {
      dispatch(updatePhotoStatus({ type: photoType, status: 'uploading' }));

      const signingOptions = await Api.getFileSigningOptions();
      const fileName = `incident_${Date.now()}_${PhotoTypes[photoType]}.jpg`;
      const uploadUrl = `${signingOptions.uploadUrl}/${fileName}?${signingOptions.sharedAccessSignature}`;

      await Api.uploadPhotoToAzure(uploadUrl, blob);

      const blobUrl = scrubBlobUrl(`${signingOptions.uploadUrl}/${fileName}`);
      if (!blobUrl) {
        throw new Error('Upload succeeded but blob URL was empty');
      }

      // Do not mark completed here — addPhoto with url is the single transition to ready
      return blobUrl;
    } catch (error) {
      Insights.trackException(error instanceof Error ? error : new Error(
        typeof error === "object" && error !== null
          ? JSON.stringify(error)
          : String(error)
      ));
      dispatch(updatePhotoStatus({ type: photoType, status: 'failed', error: 'Upload failed' }));
      throw error;
    }
  };

  const handleFileCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const photoType = currentPhotoType;
    if (!file || photoType === null) return;

    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    try {
      dispatch(addPhoto({
        type: photoType,
        photo: { uri: previewUrl, type: photoType, uploadStatus: 'pending' }
      }));

      setCurrentPhotoType(null);

      const compressedBlob = await compressImage(file);
      const finalUrl = await uploadPhotoToAzure(photoType, compressedBlob);

      dispatch(addPhoto({
        type: photoType,
        photo: { uri: previewUrl, type: photoType, url: finalUrl, uploadStatus: 'completed' }
      }));

      message.success('Photo captured and uploaded successfully');
    } catch {
      message.error('Upload failed. Tap the photo to retake.');
    }

    event.target.value = '';
  };

  const handleContinue = () => {
    if (hasInFlightRequired) {
      message.warning('Please wait for all photo uploads to complete.');
      return;
    }
    if (completedRequired < totalRequired) {
      if (notReadyRequired.length > 0) {
        message.error(
          `${notReadyRequired.length} photo upload(s) failed. Tap a red slot to retake.`
        );
      } else {
        message.warning(`Please capture all required photos (${completedRequired}/${totalRequired} completed)`);
      }
      return;
    }
    onNext();
  };

  const renderStatusOverlay = (type: PhotoTypes) => {
    const photo = photoMap[type];
    if (!photo) return null;

    if (photo.uploadStatus === 'uploading' || photo.uploadStatus === 'pending') {
      return (
        <div className="status-overlay">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 30, color: '#fff' }} spin />} />
        </div>
      );
    }
    if (isPhotoUploadReady(photo)) {
      return (
        <div className="status-overlay">
          <CheckCircleFilled style={{ fontSize: 30, color: '#52c41a' }} />
        </div>
      );
    }
    // Present without url (failed / completed-without-url after demotion)
    return (
      <div className="status-overlay">
        <CloseCircleFilled style={{ fontSize: 30, color: '#ff4d4f' }} />
      </div>
    );
  };

  const renderPhotoCard = (card: PhotoCard) => {
    const photo = photoMap[card.type];
    const showRetakeHint = !!photo && !isPhotoUploadReady(photo)
      && photo.uploadStatus !== 'pending'
      && photo.uploadStatus !== 'uploading';

    return (
      <div className="photo-card-col" key={card.type}>
        <div className="photo-card" onClick={() => handleCardTap(card.type)}>
          <div className="photo-card-content">
            <div className="photo-type-icon">
              {photo?.uri ? (
                <img
                  src={photo.uri}
                  alt={card.label}
                  className="photo-type-icon-image photo-thumbnail"
                />
              ) : card.iconName ? (
                <img
                  src={PHOTO_ICON_MAP[card.iconName]}
                  alt={card.label}
                  className="photo-type-icon-image"
                />
              ) : (
                <span style={{ fontSize: 48 }}>📷</span>
              )}
              {renderStatusOverlay(card.type)}
            </div>
            <span className="photo-type-label">{card.label}</span>
            {showRetakeHint && (
              <span className="photo-retake-hint">Tap to retake</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="step-container photo-capture-container">
      <Title level={5} style={{ textAlign: 'center' }}>Vehicle Photos</Title>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileCapture}
        style={{ display: 'none' }}
      />

      <div className="photo-progress">
        <Text>Required Photos Progress</Text>
        <Progress
          percent={progressPercent}
          status={completedRequired === totalRequired ? 'success' : 'active'}
          format={() => `${completedRequired}/${totalRequired}`}
        />
      </div>

      {notReadyRequired.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${notReadyRequired.length} required photo(s) need to be retaken`}
          description="Tap a red slot to retake."
        />
      )}

      <div className="photo-grid">
        {photoCards.map(renderPhotoCard)}
      </div>

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button size="large" onClick={onPrevious} style={{ color: '#FF9902', fontWeight: 'bold' }}>PREVIOUS</Button>
        <Button
          type="primary"
          size="large"
          onClick={handleContinue}
          disabled={completedRequired < totalRequired || hasInFlightRequired}
          style={{ fontWeight: 'bold' }}
        >
          CONTINUE
        </Button>
      </div>
    </div>
  );
};

export default PhotoCaptureStep;
